// Love (Oct 2026, the discovery plan's heart): what you told the app about a
// tune. A loved tune counts toward Top picks as strongly as anything you do
// and fills the home page's Loved shelf. Pure: the store calls these. The
// 'less' verdict (Suggest less) was retired the same month; it stays in the
// type because marks made before it are still stored and synced, and nothing
// reads them.
//
// A mark names its tune by title and credit, matched by the library's own
// identity rules (`sameTune`), because one tune is marked from places that
// know it differently: a Top pick knows the library's entry, a Recent card
// the version you opened. Persisted, shared
// between tabs, and kept in your account (migration 0018): every device merges
// the account's copy with its own tune by tune, the newer change winning
// (`mergeTaste`). A mark you take off leaves a dated removal behind for a
// while, so taking it off reaches your other devices too.

import { sameTuneKeys, tuneKeys, type TuneKeys } from '@/music/libraryIdentity';
import { stableStringify } from '@/state/accountSyncPlan';
import { parsePlaylistTuneRef, type PlaylistTuneRef } from '@/storage/playlists';

export type TasteVerdict = 'love' | 'less';

/** A tune as the buttons know it. */
export interface TasteTune {
  title: string;
  composer?: string;
  /** How to open it again, when it was marked from a tune you opened. */
  ref?: PlaylistTuneRef;
  /** A library tune only the Real Book carries: its link names the source. */
  source?: 'realbook';
}

export interface TasteMark extends TasteTune {
  verdict: TasteVerdict;
  /** When it was marked, in epoch milliseconds: what the Loved shelf orders by. */
  at: number;
  /** When it last changed, when that was after `at`: an Undo puts a mark back
   * in its place with its old `at`, but the change is new, and the newer
   * change wins when two devices disagree. */
  changedAt?: number;
}

/** Newest first. */
export type TuneTaste = TasteMark[];

/** A mark you took off, and when. */
export interface TasteRemoval {
  title: string;
  composer?: string;
  at: number;
}

/** Your marks and recent removals: what the account keeps. */
export interface TasteDoc {
  marks: TuneTaste;
  removed: TasteRemoval[];
}

/** Marks kept, so the record cannot grow without bound. */
export const TASTE_LIMIT = 500;
/** Removals kept, and for how long: long enough for every device you use to
 * hear of it. A device away longer than this can bring the mark back. */
export const REMOVAL_LIMIT = 500;
export const REMOVAL_KEEP_MS = 180 * 86_400_000;

// Every lookup compares a tune with each mark, on every store change while a
// button is on screen, so a mark's keys are worked out once.
const keyCache = new WeakMap<object, TuneKeys>();
function keysOf(tune: { title: string; composer?: string }): TuneKeys {
  let keys = keyCache.get(tune);
  if (!keys) keyCache.set(tune, (keys = tuneKeys(tune)));
  return keys;
}

/** The mark on a tune, if it has one. */
export function tasteMarkOf(
  taste: TuneTaste,
  tune: { title: string; composer?: string },
): TasteMark | undefined {
  const keys = keysOf(tune);
  return taste.find((mark) => sameTuneKeys(keysOf(mark), keys));
}

/** Love, less, or null when the tune carries no mark. */
export function tasteOf(
  taste: TuneTaste,
  tune: { title: string; composer?: string },
): TasteVerdict | null {
  return tasteMarkOf(taste, tune)?.verdict ?? null;
}

/** A mark put in its place by time, newest first. */
function insertByTime(taste: TuneTaste, mark: TasteMark): TuneTaste {
  const at = taste.findIndex((other) => other.at <= mark.at);
  const out = at < 0 ? [...taste, mark] : [...taste.slice(0, at), mark, ...taste.slice(at)];
  return out.slice(0, TASTE_LIMIT);
}

function without(taste: TuneTaste, tune: { title: string; composer?: string }): TuneTaste {
  const keys = keysOf(tune);
  return taste.filter((mark) => !sameTuneKeys(keysOf(mark), keys));
}

/**
 * Mark a tune loved or less, or (null) take its mark off. Love and less
 * exclude each other: a tune carries one mark at most. A new mark keeps the
 * way to open the tune the old one had, when this one has none.
 */
export function markTaste(
  taste: TuneTaste,
  tune: TasteTune,
  verdict: TasteVerdict | null,
  at: number,
): TuneTaste {
  const old = tasteMarkOf(taste, tune);
  const rest = without(taste, tune);
  if (!verdict) return old ? rest : taste;
  const ref = tune.ref ?? old?.ref;
  const source = tune.source ?? old?.source;
  return insertByTime(rest, {
    title: tune.title,
    ...(tune.composer ? { composer: tune.composer } : {}),
    ...(ref ? { ref } : {}),
    ...(source ? { source } : {}),
    verdict,
    at,
  });
}

/** Undo: the tune's mark as it was before (or no mark), back in its place.
 * The mark keeps its `at`, so it returns to its place on the Loved shelf;
 * `changedAt` says the change is new. */
export function restoreTasteMark(
  taste: TuneTaste,
  tune: { title: string; composer?: string },
  previous: TasteMark | null,
  at: number,
): TuneTaste {
  const rest = without(taste, tune);
  return previous ? insertByTime(rest, { ...previous, changedAt: at }) : rest;
}

/** The removals once a tune's mark changes: a mark taken off at `removedAt`
 * is noted, a tune marked again is no longer removed, and removals past
 * keeping go. */
export function noteTasteRemoval(
  removed: readonly TasteRemoval[],
  tune: { title: string; composer?: string },
  removedAt: number | null,
  now: number,
): TasteRemoval[] {
  const keys = keysOf(tune);
  const rest = removed.filter(
    (removal) => removal.at > now - REMOVAL_KEEP_MS && !sameTuneKeys(keysOf(removal), keys),
  );
  if (removedAt === null)
    return rest.length === removed.length ? (removed as TasteRemoval[]) : rest;
  const removal: TasteRemoval = {
    title: tune.title,
    ...(tune.composer ? { composer: tune.composer } : {}),
    at: removedAt,
  };
  return [removal, ...rest].sort((a, b) => b.at - a.at).slice(0, REMOVAL_LIMIT);
}

// --- merging two devices' marks ----------------------------------------------------

type Fact =
  | { mark: TasteMark; removal?: undefined; time: number }
  | { removal: TasteRemoval; mark?: undefined; time: number };

const tuneOf = (fact: Fact) => fact.mark ?? fact.removal;

/** Two facts about one tune. A title with no letters or digits has no title
 * key, so it matches only itself. */
function sameFactTune(a: Fact, b: Fact): boolean {
  const ka = keysOf(tuneOf(a));
  const kb = keysOf(tuneOf(b));
  if (!ka.key || !kb.key)
    return tuneOf(a).title === tuneOf(b).title && tuneOf(a).composer === tuneOf(b).composer;
  return sameTuneKeys(ka, kb);
}

/**
 * Two copies of your marks as one: per tune, the newest change wins, a mark
 * or a removal alike. The same answer whichever copy comes first, and merging
 * the answer again changes nothing, so devices that merge with the account
 * in any order end up agreeing. Removals past keeping are dropped first.
 */
export function mergeTaste(a: TasteDoc, b: TasteDoc, now: number): TasteDoc {
  const facts: Fact[] = [];
  for (const mark of [...a.marks, ...b.marks])
    facts.push({ mark, time: Math.max(mark.at, mark.changedAt ?? mark.at) });
  for (const removal of [...a.removed, ...b.removed])
    if (removal.at > now - REMOVAL_KEEP_MS) facts.push({ removal, time: removal.at });
  // Newest first; a tie is broken by the facts' content, never by which copy
  // they came from.
  const sorted = facts
    .map((fact) => ({ fact, text: stableStringify(fact) }))
    .sort((x, y) => y.fact.time - x.fact.time || (x.text < y.text ? -1 : x.text > y.text ? 1 : 0));
  // Facts about one tune share a title key; compare within those only.
  const winners = new Map<string, Fact[]>();
  const marks: TasteMark[] = [];
  const removed: TasteRemoval[] = [];
  for (const { fact } of sorted) {
    const keys = keysOf(tuneOf(fact));
    const bucket = keys.key || `=${tuneOf(fact).title}`;
    const known = winners.get(bucket) ?? [];
    if (known.some((winner) => sameFactTune(winner, fact))) continue;
    winners.set(bucket, [...known, fact]);
    if (fact.mark) marks.push(fact.mark);
    else removed.push(fact.removal);
  }
  const byAt = <T extends { at: number }>(list: T[]) =>
    list
      .map((item) => ({ item, text: stableStringify(item) }))
      .sort((x, y) => y.item.at - x.item.at || (x.text < y.text ? -1 : x.text > y.text ? 1 : 0))
      .map(({ item }) => item);
  return {
    marks: byAt(marks).slice(0, TASTE_LIMIT),
    removed: byAt(removed).slice(0, REMOVAL_LIMIT),
  };
}

/** A removal list read back from storage or the account, guarded. */
export function normalizeTasteRemovals(raw: unknown): TasteRemoval[] {
  if (!Array.isArray(raw)) return [];
  const out: TasteRemoval[] = [];
  for (const value of raw) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const entry = value as Record<string, unknown>;
    const title = typeof entry.title === 'string' && entry.title.trim() ? entry.title : null;
    const at =
      typeof entry.at === 'number' && Number.isFinite(entry.at) && entry.at >= 0 ? entry.at : null;
    if (!title || at === null) continue;
    const composer = typeof entry.composer === 'string' && entry.composer ? entry.composer : null;
    out.push({ title, ...(composer ? { composer } : {}), at });
  }
  return out.sort((a, b) => b.at - a.at).slice(0, REMOVAL_LIMIT);
}

/** The account's copy of your marks, guarded field by field. */
export function normalizeTasteDoc(raw: unknown): TasteDoc {
  const doc = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const record = doc as Record<string, unknown>;
  return {
    marks: normalizeTuneTaste(record.marks),
    removed: normalizeTasteRemovals(record.removed),
  };
}

/** The loved tunes, newest first: the home page's Loved shelf. */
export function lovedTunes(taste: TuneTaste): TasteMark[] {
  return taste.filter((mark) => mark.verdict === 'love');
}

/** A persisted or another tab's marks, guarded field by field: a mark with
 * no title, verdict or time is dropped, a bad ref or source left out, and a
 * tune marked twice keeps its newest mark. */
export function normalizeTuneTaste(raw: unknown): TuneTaste {
  if (!Array.isArray(raw)) return [];
  const marks: TasteMark[] = [];
  for (const value of raw) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const entry = value as Record<string, unknown>;
    const title = typeof entry.title === 'string' && entry.title.trim() ? entry.title : null;
    const verdict = entry.verdict === 'love' || entry.verdict === 'less' ? entry.verdict : null;
    const at =
      typeof entry.at === 'number' && Number.isFinite(entry.at) && entry.at >= 0 ? entry.at : null;
    if (!title || !verdict || at === null) continue;
    const composer = typeof entry.composer === 'string' && entry.composer ? entry.composer : null;
    const ref = parsePlaylistTuneRef(entry.ref);
    const changedAt =
      typeof entry.changedAt === 'number' &&
      Number.isFinite(entry.changedAt) &&
      entry.changedAt > at
        ? entry.changedAt
        : null;
    marks.push({
      title,
      ...(composer ? { composer } : {}),
      ...(ref ? { ref } : {}),
      ...(entry.source === 'realbook' ? { source: 'realbook' as const } : {}),
      verdict,
      at,
      ...(changedAt !== null ? { changedAt } : {}),
    });
  }
  marks.sort((a, b) => b.at - a.at);
  const out: TuneTaste = [];
  for (const mark of marks) {
    if (tasteMarkOf(out, mark)) continue;
    out.push(mark);
    if (out.length === TASTE_LIMIT) break;
  }
  return out;
}
